import PannellumViewer from '../PannellumViewer';
import FadeIn from '@/shared/components/FadeIn';

const FEATURES = [
  {
    number: '01',
    label: '360° Jobsite Capture',
    heading: 'Every corner.\nEvery floor.\nEvery day.',
    body: 'Our technicians walk every level of your project with professional 360-degree cameras, creating a fully navigable virtual jobsite. Stakeholders anywhere in the world can tour the site as if they were standing on it.',
    image: '/static/images/L-182.jpg',
    imageAlt: '360-degree hallway documentation',
    imageSide: 'right' as const,
    panorama: true,
  },
  {
    number: '02',
    label: 'Drone Documentation',
    heading: 'The full picture,\nfrom above.',
    body: 'High-resolution drone captures at every deck milestone give owners, GCs, and inspectors an aerial perspective of progress that ground-level walkthroughs simply can\'t provide. Permanent records for every pour.',
    image: '/static/images/pt_deck_1600.webp',
    imageAlt: 'Drone aerial deck documentation',
    imageSide: 'left' as const,
    panorama: false,
  },
  {
    number: '03',
    label: 'Underground Documentation',
    heading: 'What\'s buried\nstays visible.',
    body: 'Before concrete is poured, we capture underground utilities, rebar, and infrastructure in meticulous detail. When questions arise years later — about clashes, as-built accuracy, or maintenance — the record is there.',
    image: '/static/images/sog.webp',
    imageAlt: 'Underground construction documentation',
    imageSide: 'right' as const,
    panorama: false,
  },
  {
    number: '04',
    label: 'Permanent Progress Records',
    heading: 'A record that\noutlives the project.',
    body: 'Every capture is archived and indexed against the project timeline. Long after the crew has left, owners have a fully searchable, permanent documentation library for warranty claims, renovations, and facility management.',
    image: '/static/images/viewer.webp',
    imageAlt: 'Construction progress tracking',
    imageSide: 'left' as const,
    panorama: false,
  },
];

const FeatureNarrativeSection: React.FC = () => {
  return (
    <div id="features">
      {FEATURES.map((feature) => {
        const isRight = feature.imageSide === 'right';
        return (
          <section
            key={feature.number}
            className="relative overflow-hidden bg-maintenance-dark"
          >
            <div className={`flex flex-col ${isRight ? 'lg:flex-row' : 'lg:flex-row-reverse'} min-h-[85vh] w-full`}>
              {/* Text side */}
              <div className="relative z-10 flex items-center w-full lg:w-[45%] min-w-0 px-8 md:px-14 lg:px-20 py-20 lg:py-0">
                <FadeIn className="w-full">
                  {/* Large background number */}
                  <span
                    aria-hidden="true"
                    className="absolute top-6 left-6 lg:top-10 lg:left-10 text-[120px] lg:text-[180px] font-black text-white/[0.04] leading-none select-none pointer-events-none"
                  >
                    {feature.number}
                  </span>

                  <p className="text-maintenance-cyan text-xs uppercase tracking-widest font-semibold mb-4">{feature.label}</p>
                  <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white leading-[1.05] mb-6 whitespace-pre-line">
                    {feature.heading}
                  </h2>
                  <p className="text-white/60 text-base md:text-lg leading-relaxed max-w-md">
                    {feature.body}
                  </p>

                  {/* Accent rule */}
                  <div className="mt-10 w-12 h-1 bg-maintenance-cyan rounded-full" />
                </FadeIn>
              </div>

              {/* Image side — edge-to-edge, no padding */}
              <div className="relative w-full lg:w-[55%] h-72 sm:h-96 lg:h-auto min-h-[400px] overflow-hidden">
                {feature.panorama ? (
                  <PannellumViewer
                    src={feature.image}
                    autoRotate={-6}
                    draggable
                    hfov={110}
                    className="w-full h-full"
                  />
                ) : (
                  <img
                    src={feature.image}
                    alt={feature.imageAlt}
                    className="absolute inset-0 w-full h-full object-cover object-center"
                  />
                )}
                {/* Blend the image into the dark background on the text-facing side */}
                <div
                  className={`absolute inset-0 ${
                    isRight
                      ? 'bg-gradient-to-r from-maintenance-dark via-transparent to-transparent'
                      : 'bg-gradient-to-l from-maintenance-dark via-transparent to-transparent'
                  }`}
                />
                {/* Top + bottom edge fades for seamless section stacking */}
                <div className="absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-maintenance-dark to-transparent" />
                <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-maintenance-dark to-transparent" />
              </div>
            </div>

            {/* Subtle bottom separator */}
            <div className="absolute inset-x-0 bottom-0 h-px bg-white/5" />
          </section>
        );
      })}
    </div>
  );
};

export default FeatureNarrativeSection;
